import moment, { Moment } from 'moment'

import { AudienceFootprint, DeviceSettings, InventorySettings } from 'api/interfaces'
import { UploadedImage } from 'components/Basic/AcImageUploader'
import { UploadedVideo } from 'components/Basic/AcVideoUploader'

import { emptyFootprint } from './FootprintSelector'

export const minQuickCampaignBudget = 100

export enum CreativeLinkMode {
  Shared = 'Shared',
  Unique = 'Unique'
}

export enum QuickCampaignChannel {
  Internet = 'internet',
  Tv = 'tv'
}

export enum QuickCampaignResultStatus {
  Created = 'Created',
  CreatedWithWarnings = 'CreatedWithWarnings',
  Failed = 'Failed'
}

export const channelLabels: Record<QuickCampaignChannel, string> = {
  [QuickCampaignChannel.Internet]: 'Internet',
  [QuickCampaignChannel.Tv]: 'TV'
}

const channelNameSuffixes: Record<QuickCampaignChannel, string> = {
  [QuickCampaignChannel.Internet]: 'IP',
  [QuickCampaignChannel.Tv]: 'TV'
}

export interface CreativeLinks {
  mode: CreativeLinkMode
  sharedLink: string
  linksByCreativeUrl: Record<string, string>
}

export interface ChannelDraftBase {
  isSelected: boolean
  budget: number
  isUploading: boolean
  links: CreativeLinks
}

export interface InternetChannelDraft extends ChannelDraftBase {
  impressionFrequency: number
  targetCPM: number
  inventorySettings: InventorySettings
  deviceSettings: DeviceSettings
  uploadedImages: UploadedImage[]
  invalidImages: UploadedImage[]
}

export interface TvChannelDraft extends ChannelDraftBase {
  uploadedVideos: UploadedVideo[]
}

export interface QuickCampaignChannels {
  internet: InternetChannelDraft
  tv: TvChannelDraft
}

export interface QuickCampaignShared {
  campaignName: string
  start: Moment | null
  end: Moment | null
  audienceUrl: string
  addressCount: number
  isValidUpload: boolean
  isAudienceCounting: boolean
  isAudienceUploading: boolean
  footprint: AudienceFootprint
}

export interface QuickCampaignResult {
  channel: QuickCampaignChannel
  campaignName: string
  campaignId?: string
  status: QuickCampaignResultStatus
  message?: string
}

const newCreativeLinks = (): CreativeLinks => ({ mode: CreativeLinkMode.Shared, sharedLink: '', linksByCreativeUrl: {} })

export const newQuickCampaignChannels = (): QuickCampaignChannels => ({
  internet: {
    isSelected: true,
    budget: 250,
    impressionFrequency: 6,
    targetCPM: 7,
    inventorySettings: { targetApp: false, targetWeb: true },
    deviceSettings: { targetPc: true, targetPhone: true, targetTablet: true },
    uploadedImages: [],
    invalidImages: [],
    isUploading: false,
    links: newCreativeLinks()
  },
  tv: {
    isSelected: false,
    budget: 250,
    uploadedVideos: [],
    isUploading: false,
    links: newCreativeLinks()
  }
})

export const newQuickCampaignShared = (): QuickCampaignShared => ({
  campaignName: '',
  start: null,
  end: null,
  audienceUrl: '',
  addressCount: 0,
  isValidUpload: true,
  isAudienceCounting: false,
  isAudienceUploading: false,
  footprint: emptyFootprint()
})

export const selectedChannelsOf = (channels: QuickCampaignChannels): QuickCampaignChannel[] => [...(channels.internet.isSelected ? [QuickCampaignChannel.Internet] : []), ...(channels.tv.isSelected ? [QuickCampaignChannel.Tv] : [])]

export const campaignNameFor = (baseName: string, channel: QuickCampaignChannel, channels: QuickCampaignChannels): string => {
  const trimmedName = baseName.trim()
  return selectedChannelsOf(channels).length > 1 ? `${trimmedName} ${channelNameSuffixes[channel]}` : trimmedName
}

export const validImages = (internet: InternetChannelDraft): UploadedImage[] => internet.uploadedImages.filter(image => !internet.invalidImages.some(invalid => invalid.url === image.url))

export const creativeUrlsOf = (channels: QuickCampaignChannels, channel: QuickCampaignChannel): string[] => (channel === QuickCampaignChannel.Internet ? validImages(channels.internet).map(image => image.url) : channels.tv.uploadedVideos.map(video => video.url))

export const resolveCreativeLink = (links: CreativeLinks, creativeUrl: string): string => (links.mode === CreativeLinkMode.Shared ? links.sharedLink : links.linksByCreativeUrl[creativeUrl] ?? '').trim()

export const isValidUrl = (value: string): boolean => {
  try {
    return Boolean(new URL(value.trim()))
  } catch {
    return false
  }
}

export const isBudgetValid = (budget: number): boolean => budget >= minQuickCampaignBudget

const areLinksValid = (links: CreativeLinks, creativeUrls: string[]): boolean => (links.mode === CreativeLinkMode.Shared ? isValidUrl(links.sharedLink) : creativeUrls.every(url => isValidUrl(resolveCreativeLink(links, url))))

const linksMissingLabel = (links: CreativeLinks) => (links.mode === CreativeLinkMode.Shared ? 'website link' : 'a valid link on every creative')

export const isStartDateValid = (shared: QuickCampaignShared): boolean => !!shared.start && !shared.start.isBefore(moment(), 'day')

export const isEndDateValid = (shared: QuickCampaignShared): boolean => !!shared.end && !!shared.start && shared.end.isAfter(shared.start)

export const isAudienceValid = (shared: QuickCampaignShared): boolean => shared.audienceUrl.length > 0 && shared.addressCount > 0 && shared.isValidUpload

const missingSharedItemsOf = (shared: QuickCampaignShared, channels: QuickCampaignChannels): string[] => [
  ...(shared.campaignName.trim().length > 0 ? [] : ['Campaign name']),
  ...(selectedChannelsOf(channels).length > 0 ? [] : ['Campaign type']),
  ...(isStartDateValid(shared) ? [] : ['Start date']),
  ...(isEndDateValid(shared) ? [] : ['End date after the start date']),
  ...(isAudienceValid(shared) || shared.isAudienceCounting || shared.isAudienceUploading ? [] : ['Target audience'])
]

const missingInternetItemsOf = (internet: InternetChannelDraft): string[] => {
  const images = validImages(internet)
  return [
    ...(isBudgetValid(internet.budget) ? [] : [`Internet budget of at least $${minQuickCampaignBudget}`]),
    ...(internet.impressionFrequency > 0 ? [] : ['Internet views per user per week']),
    ...(internet.targetCPM > 0 ? [] : ['Internet target CPM']),
    ...(images.length > 0 ? [] : ['Internet display ads']),
    ...(images.length === 0 ||
    areLinksValid(
      internet.links,
      images.map(image => image.url)
    )
      ? []
      : [`Internet ${linksMissingLabel(internet.links)}`])
  ]
}

const missingTvItemsOf = (tv: TvChannelDraft): string[] => [
  ...(isBudgetValid(tv.budget) ? [] : [`TV budget of at least $${minQuickCampaignBudget}`]),
  ...(tv.uploadedVideos.length > 0 ? [] : ['TV ads']),
  ...(tv.uploadedVideos.length === 0 ||
  areLinksValid(
    tv.links,
    tv.uploadedVideos.map(video => video.url)
  )
    ? []
    : [`TV ${linksMissingLabel(tv.links)}`])
]

export const missingItemsOf = (shared: QuickCampaignShared, channels: QuickCampaignChannels): string[] => [...missingSharedItemsOf(shared, channels), ...(channels.internet.isSelected ? missingInternetItemsOf(channels.internet) : []), ...(channels.tv.isSelected ? missingTvItemsOf(channels.tv) : [])]

export const isAnyChannelUploading = (channels: QuickCampaignChannels): boolean => (channels.internet.isSelected && channels.internet.isUploading) || (channels.tv.isSelected && channels.tv.isUploading)
